import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Clock,
  Users,
  FileText,
  Frown,
  DollarSign,
  Rocket,
  Banknote,
  Star,
  UserCheck,
  TrendingUp,
  Shield,
  CheckCircle2,
  ClipboardCheck,
  FileCheck,
  Headphones,
  Wrench,
  Phone,
  MessageCircle,
  Mail,
  Menu,
  X,
  ChevronRight,
  Zap,
  Award,
  MapPin,
  Printer,
  Cookie,
  ExternalLink,
} from "lucide-react";

// Layer 3: React Hook Defense - useRemoveWatermark
const useRemoveWatermark = () => {
  const removeWatermarks = useCallback(() => {
    // Remove by ID
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.remove();
    
    // Remove all elements after #root
    const root = document.getElementById('root');
    if (root) {
      let sibling = root.nextElementSibling;
      while (sibling) {
        const next = sibling.nextElementSibling;
        if (sibling.tagName !== 'SCRIPT') {
          sibling.remove();
        }
        sibling = next;
      }
    }
    
    // Remove by various selectors
    const selectors = [
      '[data-emergent-badge]',
      '[class*="emergent"]',
      '[id*="emergent"]',
      'a[href*="emergent.sh"]',
      'iframe[src*="emergent"]',
      'div[style*="Made with"]',
      'a[href*="emergent"]'
    ];
    
    selectors.forEach(selector => {
      try {
        document.querySelectorAll(selector).forEach(el => el.remove());
      } catch (e) { /* ignore */ }
    });
    
    // Remove fixed position elements in bottom-right corner
    document.querySelectorAll('body > *').forEach(el => {
      if (el.id === 'root' || el.tagName === 'SCRIPT' || el.tagName === 'NOSCRIPT') return;
      const style = window.getComputedStyle(el);
      if (style.position === 'fixed') {
        const bottom = parseInt(style.bottom) || 0;
        const right = parseInt(style.right) || 0;
        if (bottom < 100 && right < 250) {
          el.remove();
        }
      }
    });
  }, []);

  useEffect(() => {
    // Initial cleanup
    removeWatermarks();
    
    // Multiple delayed cleanups
    const timeouts = [100, 500, 1000, 2000, 3000, 5000].map(delay =>
      setTimeout(removeWatermarks, delay)
    );
    
    // MutationObserver for dynamic injection
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          removeWatermarks();
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Periodic cleanup
    const interval = setInterval(removeWatermarks, 5000);
    
    // Cleanup on unmount
    return () => {
      timeouts.forEach(clearTimeout);
      clearInterval(interval);
      observer.disconnect();
    };
  }, [removeWatermarks]);
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_projekt-finish/artifacts/12ei5kvn_ENTHIDA%201%20Black%20v2.png";
const HERO_BG_URL = "https://customer-assets.emergentagent.com/job_projekt-finish/artifacts/5vk3brnn_2.jpg";
const WHY_ENTHIDA_IMG = "https://customer-assets.emergentagent.com/job_projekt-finish/artifacts/qwrdeqz6_IMG_6062.jpeg";

// Cookie Consent Banner
const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setShowBanner(false);
  };

  const rejectCookies = () => {
    localStorage.setItem("cookieConsent", "rejected");
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[100] p-3 bg-[#121826]/95 backdrop-blur-xl border-t border-white/10 no-print"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-gray-300 text-xs sm:text-sm text-center sm:text-left">
          🍪 Wir nutzen Cookies für die Funktion der Website.{" "}
          <button
            onClick={() => document.getElementById("datenschutz-modal")?.classList.remove("hidden")}
            className="text-[#00CED1] hover:underline"
          >
            Mehr erfahren
          </button>
        </p>
        <div className="flex gap-2 flex-shrink-0">
          <button
            data-testid="cookie-reject"
            onClick={rejectCookies}
            className="px-4 py-2 text-xs font-medium text-gray-300 border border-white/20 rounded-full hover:bg-white/5 transition-all"
          >
            Ablehnen
          </button>
          <button
            data-testid="cookie-accept"
            onClick={acceptCookies}
            className="px-4 py-2 text-xs font-bold text-black bg-[#39FF14] rounded-full hover:bg-[#32e011] transition-all"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal Component
const Modal = ({ id, title, children, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div
      id={id}
      data-testid={id}
      className="hidden fixed inset-0 z-[200] overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-screen px-4 py-8 flex items-start justify-center">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
        <div
          className="relative bg-[#121826] border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-[#121826] border-b border-white/10 px-8 py-5 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">{title}</h2>
            <button
              data-testid={`${id}-close`}
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="px-8 py-6 prose prose-invert max-w-none">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// Impressum Content
const ImpressumContent = () => (
  <div className="text-gray-300 space-y-6">
    <section>
      <h3 className="text-xl font-semibold text-white mb-3">Angaben gemäß § 5 TMG</h3>
      <p>
        <strong className="text-white">ENTHIDA</strong><br />
        Smart Energy Systems<br />
        Enes Dedic<br />
        Ludwig-Stempfl-Straße 17<br />
        84307 Eggenfelden<br />
        Deutschland
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">Kontakt</h3>
      <p>
        Telefon: +49 176 41234 614<br />
        E-Mail: info@enthida.de
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">Berufsbezeichnung und berufsrechtliche Regelungen</h3>
      <p>
        Berufsbezeichnung: Elektromeister<br />
        Zuständige Kammer: HWK Niederbayern-Oberpfalz<br />
        Verliehen in: Deutschland
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">Umsatzsteuer-ID</h3>
      <p>
        Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
        <span className="text-[#00CED1]">[USt-IdNr. hier eintragen]</span>
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h3>
      <p>
        Enes Dedic<br />
        Ludwig-Stempfl-Straße 17<br />
        84307 Eggenfelden
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">EU-Streitschlichtung</h3>
      <p>
        Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
        <a 
          href="https://ec.europa.eu/consumers/odr/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-[#39FF14] hover:underline inline-flex items-center gap-1"
        >
          https://ec.europa.eu/consumers/odr/
          <ExternalLink className="w-3 h-3" />
        </a>
        <br />
        Unsere E-Mail-Adresse finden Sie oben im Impressum.
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">Verbraucherstreitbeilegung / Universalschlichtungsstelle</h3>
      <p>
        Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer 
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>
    </section>

    <section className="pt-4 border-t border-white/10">
      <p className="text-sm text-gray-500">
        <strong className="text-[#00CED1]">Hinweis:</strong> Die in türkis markierten Felder sind Platzhalter und müssen 
        mit Ihren tatsächlichen Daten ersetzt werden.
      </p>
    </section>
  </div>
);

// Datenschutz Content
const DatenschutzContent = () => (
  <div className="text-gray-300 space-y-6">
    <section>
      <h3 className="text-xl font-semibold text-white mb-3">1. Datenschutz auf einen Blick</h3>
      <h4 className="text-lg font-medium text-white mb-2">Allgemeine Hinweise</h4>
      <p>
        Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten 
        passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie 
        persönlich identifiziert werden können.
      </p>
    </section>

    <section>
      <h4 className="text-lg font-medium text-white mb-2">Datenerfassung auf dieser Website</h4>
      <p className="mb-2">
        <strong className="text-white">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong>
      </p>
      <p className="mb-4">
        Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
        können Sie dem Impressum dieser Website entnehmen.
      </p>
      <p className="mb-2">
        <strong className="text-white">Wie erfassen wir Ihre Daten?</strong>
      </p>
      <p>
        Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B. 
        um Daten handeln, die Sie in ein Kontaktformular eingeben oder uns per E-Mail, Telefon oder WhatsApp senden.
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">2. Hosting</h3>
      <p>
        Wir hosten die Inhalte unserer Website bei folgendem Anbieter:
      </p>
      <p className="mt-2">
        <span className="text-[#00CED1]">[Name des Hosting-Anbieters eintragen]</span>
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">3. Allgemeine Hinweise und Pflichtinformationen</h3>
      <h4 className="text-lg font-medium text-white mb-2">Datenschutz</h4>
      <p>
        Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre 
        personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften sowie 
        dieser Datenschutzerklärung.
      </p>
    </section>

    <section>
      <h4 className="text-lg font-medium text-white mb-2">Hinweis zur verantwortlichen Stelle</h4>
      <p>
        Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
      </p>
      <p className="mt-2">
        ENTHIDA<br />
        Enes Dedic<br />
        <span className="text-[#00CED1]">[Adresse]</span><br />
        Telefon: +49 176 41234 614<br />
        E-Mail: info@enthida.de
      </p>
    </section>

    <section>
      <h4 className="text-lg font-medium text-white mb-2">Widerruf Ihrer Einwilligung zur Datenverarbeitung</h4>
      <p>
        Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können eine 
        bereits erteilte Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten 
        Datenverarbeitung bleibt vom Widerruf unberührt.
      </p>
    </section>

    <section>
      <h4 className="text-lg font-medium text-white mb-2">Beschwerderecht bei der zuständigen Aufsichtsbehörde</h4>
      <p>
        Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei einer Aufsichtsbehörde zu. 
        Das Beschwerderecht besteht unbeschadet anderweitiger verwaltungsrechtlicher oder gerichtlicher Rechtsbehelfe.
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">4. Datenerfassung auf dieser Website</h3>
      <h4 className="text-lg font-medium text-white mb-2">Cookies</h4>
      <p className="mb-4">
        Unsere Internetseiten verwenden so genannte „Cookies". Cookies sind kleine Datenpakete und richten auf 
        Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für die Dauer einer Sitzung 
        (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät gespeichert.
      </p>
      <p className="mb-4">
        <strong className="text-white">Notwendige Cookies:</strong> Diese Cookies sind für den Betrieb der Website 
        erforderlich. Hierzu zählt z.B. die Speicherung Ihrer Cookie-Einstellungen.
      </p>
      <p>
        Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden und 
        Cookies nur im Einzelfall erlauben, die Annahme von Cookies für bestimmte Fälle oder generell ausschließen 
        sowie das automatische Löschen der Cookies beim Schließen des Browsers aktivieren.
      </p>
    </section>

    <section>
      <h4 className="text-lg font-medium text-white mb-2">Kontaktaufnahme</h4>
      <p>
        Wenn Sie uns per E-Mail, Telefon oder WhatsApp kontaktieren, wird Ihre Anfrage inklusive aller daraus 
        hervorgehenden personenbezogenen Daten (Name, Anfrage, Telefonnummer) zum Zwecke der Bearbeitung Ihres 
        Anliegens bei uns gespeichert und verarbeitet. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
      </p>
      <p className="mt-2">
        Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage 
        mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist.
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">5. Analyse-Tools und Werbung</h3>
      <p>
        <span className="text-[#00CED1]">[Falls Sie Google Analytics oder andere Analyse-Tools verwenden, 
        hier die entsprechenden Informationen eintragen. Falls nicht, diesen Abschnitt entfernen oder anpassen:]</span>
      </p>
      <p className="mt-2">
        Diese Website verwendet derzeit keine Analyse-Tools oder Tracking-Dienste.
      </p>
    </section>

    <section>
      <h3 className="text-xl font-semibold text-white mb-3">6. Ihre Rechte</h3>
      <p>Sie haben jederzeit das Recht:</p>
      <ul className="list-disc list-inside mt-2 space-y-1">
        <li>Auskunft über Ihre bei uns gespeicherten Daten zu erhalten</li>
        <li>Berichtigung unrichtiger personenbezogener Daten zu verlangen</li>
        <li>Löschung Ihrer bei uns gespeicherten Daten zu verlangen</li>
        <li>Einschränkung der Datenverarbeitung zu verlangen</li>
        <li>Der Datenverarbeitung zu widersprechen</li>
        <li>Datenübertragbarkeit zu verlangen</li>
      </ul>
    </section>

    <section className="pt-4 border-t border-white/10">
      <p className="text-sm text-gray-500">
        <strong className="text-[#00CED1]">Hinweis:</strong> Die in türkis markierten Felder sind Platzhalter und müssen 
        mit Ihren tatsächlichen Daten ersetzt werden. Diese Datenschutzerklärung dient als Vorlage und sollte 
        von einem Rechtsanwalt geprüft werden.
      </p>
      <p className="text-sm text-gray-500 mt-2">
        Stand: Dezember 2025
      </p>
    </section>
  </div>
);

// Navigation Links
const NAV_LINKS = [
  { href: "#problem", label: "Das Problem" },
  { href: "#loesung", label: "Die Lösung" },
  { href: "#vorteile", label: "Vorteile" },
  { href: "#zusammenarbeit", label: "Ablauf" },
  { href: "#leistungen", label: "Leistungen" },
  { href: "#faq", label: "FAQ" },
  { href: "#kontakt", label: "Kontakt" },
];

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 no-print ${
        isScrolled
          ? "backdrop-blur-xl bg-[#0a0f1a]/90 border-b border-white/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#" data-testid="logo-link" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="ENTHIDA Logo" className="h-10 w-auto" />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-${link.href.slice(1)}`}
              className="text-sm font-medium text-gray-300 hover:text-[#39FF14] transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a
            href="tel:+4917641234614"
            data-testid="header-cta-phone"
            className="bg-[#39FF14] text-black font-bold px-5 py-2.5 rounded-full hover:bg-[#32e011] hover:shadow-[0_0_20px_rgba(57,255,20,0.4)] transition-all flex items-center gap-2"
          >
            <Phone className="w-4 h-4" />
            Anrufen
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden p-2 text-white"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden mobile-menu-enter fixed inset-0 top-16 bg-[#0a0f1a]/98 backdrop-blur-xl z-40"
        >
          <nav className="flex flex-col items-center gap-6 pt-10">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-lg font-medium text-gray-300 hover:text-[#39FF14] transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a
              href="tel:+4917641234614"
              className="mt-4 bg-[#39FF14] text-black font-bold px-6 py-3 rounded-full"
            >
              <Phone className="w-5 h-5 inline mr-2" />
              Jetzt anrufen
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => (
  <section
    data-testid="hero-section"
    className="relative min-h-screen flex items-center justify-center hero-bg pt-20"
    style={{
      backgroundImage: `linear-gradient(to bottom, rgba(10,15,26,0.85), rgba(10,15,26,0.95)), url(${HERO_BG_URL})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    <div className="max-w-7xl mx-auto px-6 py-24 relative z-10">
      <div className="max-w-4xl">
        <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-6">
          Partnerschaft für Installateure
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6 leading-tight">
          Sie montieren.<br />
          <span className="text-[#39FF14] text-glow-primary">Wir schließen ab.</span>
        </h1>
        <p className="text-lg sm:text-xl text-gray-300 leading-relaxed mb-8 max-w-2xl">
          Als Solarfirma oder Elektrobetrieb kennen Sie das Problem: Die Montage läuft, aber für 
          Abnahme, Dokumentation und Kundeneinweisung fehlt die Zeit – oder das Personal. Genau 
          hier setzen wir an.
        </p>
        <p className="text-base text-gray-400 mb-10 max-w-2xl">
          ENTHIDA übernimmt den technischen Abschluss Ihrer PV-Projekte, damit Sie sich auf das 
          konzentrieren können, was Sie am besten können: <strong className="text-white">Anlagen montieren.</strong>
        </p>
        <div className="flex flex-wrap gap-4">
          <a
            href="#kontakt"
            data-testid="hero-cta-contact"
            className="bg-[#39FF14] text-black font-bold px-8 py-4 rounded-full hover:bg-[#32e011] hover:shadow-[0_0_20px_rgba(57,255,20,0.4)] transition-all flex items-center gap-2 btn-pulse"
          >
            Jetzt Partnerschaft starten
            <ChevronRight className="w-5 h-5" />
          </a>
          <a
            href="#loesung"
            data-testid="hero-cta-learn-more"
            className="border border-white/20 text-white font-bold px-8 py-4 rounded-full hover:border-white hover:bg-white/5 transition-all flex items-center gap-2"
          >
            Mehr erfahren
          </a>
        </div>
      </div>
    </div>
    
    {/* Decorative Elements */}
    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0f1a] to-transparent pointer-events-none" />
  </section>
);

// Problem Section
const ProblemSection = () => {
  const problems = [
    { icon: Clock, title: "Zeitdruck", desc: "Die nächste Baustelle wartet, aber die Abnahme der letzten Anlage steht noch aus" },
    { icon: Users, title: "Fachkräftemangel", desc: "Qualifizierte Elektriker für Abnahmen sind schwer zu finden" },
    { icon: FileText, title: "Dokumentationsstau", desc: "Protokolle, Netzbetreiber-Anmeldungen, Marktstammdatenregister – alles bleibt liegen" },
    { icon: Frown, title: "Unzufriedene Kunden", desc: "Warten auf die Inbetriebnahme und werden ungeduldig" },
    { icon: DollarSign, title: "Offene Rechnungen", desc: "Schlussrechnung kann erst nach Abnahme gestellt werden" },
  ];

  return (
    <section id="problem" data-testid="problem-section" className="py-24 sm:py-32 bg-[#0a0f1a]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-4">
            Das Problem
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
            Kommt Ihnen das bekannt vor?
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {problems.map((problem, index) => (
            <div
              key={index}
              data-testid={`problem-card-${index}`}
              className="bg-[#121826] border border-white/10 rounded-2xl p-8 card-hover"
            >
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-5">
                <problem.icon className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{problem.title}</h3>
              <p className="text-gray-400">{problem.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Das alles kostet Sie Geld, Nerven und im schlimmsten Fall Ihren guten Ruf.<br />
            <span className="text-[#39FF14] font-semibold">Es gibt einen besseren Weg.</span>
          </p>
        </div>
      </div>
    </section>
  );
};

// Lösung Section
const LoesungSection = () => {
  const solutions = [
    {
      icon: CheckCircle2,
      title: "Abnahme & Inbetriebnahme",
      items: [
        "Professionelle Prüfung der kompletten Anlage",
        "Messungen nach VDE 0100-600 / VDE 0126",
        "Inbetriebnahmeprotokoll mit allen Messwerten",
        "Funktionstest aller Komponenten",
      ],
    },
    {
      icon: ClipboardCheck,
      title: "Dokumentation & Anmeldung",
      items: [
        "Vollständige Anlagendokumentation",
        "Fertigmeldung beim Netzbetreiber",
        "Unterstützung bei Marktstammdatenregister",
        "Übergabefertige Unterlagen für Ihren Kunden",
      ],
    },
    {
      icon: Headphones,
      title: "Kundeneinweisung",
      items: [
        "Verständliche Erklärung der Anlage",
        "Einweisung in App/Monitoring",
        "Beantwortung aller Kundenfragen",
        "Professioneller Projektabschluss",
      ],
    },
    {
      icon: Wrench,
      title: "Problemlösung",
      items: [
        "Fehlersuche bei Kommunikationsproblemen",
        "Analyse von Ertragsabweichungen",
        "Unterstützung bei kniffligen Fällen",
        "Schnelle Identifikation typischer Fehlerquellen",
      ],
    },
  ];

  return (
    <section id="loesung" data-testid="loesung-section" className="py-24 sm:py-32 bg-[#121826] grain-texture">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-4">
            Die Lösung
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
            ENTHIDA – Ihr Partner für den technischen Abschluss
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Wir sind spezialisiert auf die letzte, entscheidende Phase Ihrer PV-Projekte. 
            Während Ihr Team bereits die nächste Anlage montiert, kümmern wir uns um:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {solutions.map((solution, index) => (
            <div
              key={index}
              data-testid={`solution-card-${index}`}
              className="bg-[#0a0f1a] border border-white/10 rounded-2xl p-8 card-hover group"
            >
              <div className="w-14 h-14 rounded-xl bg-[#39FF14]/10 flex items-center justify-center mb-6 group-hover:bg-[#39FF14]/20 transition-colors">
                <solution.icon className="w-7 h-7 text-[#39FF14]" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">{solution.title}</h3>
              <ul className="space-y-3">
                {solution.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-400">
                    <Zap className="w-4 h-4 text-[#00CED1] mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Vorteile Section
const VorteileSection = () => {
  const vorteile = [
    { icon: Rocket, title: "Mehr Projekte abschließen", desc: "Kein Projekt bleibt mehr liegen. Ihre Montage ist fertig? Wir übernehmen den Rest innerhalb weniger Tage." },
    { icon: Banknote, title: "Schneller zur Schlussrechnung", desc: "Je schneller die Abnahme, desto schneller fließt Ihr Geld. Keine wochenlangen Verzögerungen mehr." },
    { icon: Star, title: "Zufriedenere Kunden", desc: "Professionelle Einweisung und saubere Übergabe = begeisterte Kunden = 5-Sterne-Bewertungen." },
    { icon: UserCheck, title: "Flexibilität ohne Festanstellung", desc: "Nutzen Sie unsere Kapazitäten nach Bedarf. Keine Fixkosten, keine Sozialabgaben, keine Urlaubsplanung." },
    { icon: TrendingUp, title: "Skalieren ohne Risiko", desc: "Mehr Aufträge annehmen, ohne mehr Personal einzustellen. Wir wachsen mit Ihrem Bedarf." },
    { icon: Shield, title: "Haftung & Qualität", desc: "Elektromeister-Qualifikation und Berufshaftpflicht. Sie können sich auf geprüfte Qualität verlassen." },
  ];

  return (
    <section id="vorteile" data-testid="vorteile-section" className="py-24 sm:py-32 bg-[#0a0f1a]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-4">
            Ihre Vorteile
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Was Sie davon haben
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vorteile.map((vorteil, index) => (
            <div
              key={index}
              data-testid={`vorteil-card-${index}`}
              className="bg-[#121826] border border-white/10 rounded-2xl p-8 card-hover"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#39FF14]/20 to-[#00CED1]/20 flex items-center justify-center mb-5">
                <vorteil.icon className="w-6 h-6 text-[#39FF14]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">{vorteil.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{vorteil.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Zusammenarbeit Section (Timeline)
const ZusammenarbeitSection = () => {
  const steps = [
    {
      num: "01",
      title: "Projekt melden",
      desc: "Sie informieren uns über ein abgeschlossenes Montageprojekt – per WhatsApp, E-Mail oder Telefon. Kurze Angaben zur Anlage genügen.",
    },
    {
      num: "02",
      title: "Termin koordinieren",
      desc: "Wir stimmen einen Termin direkt mit Ihrem Kunden ab. Sie müssen sich um nichts kümmern – oder Sie übergeben uns die Kontaktdaten.",
    },
    {
      num: "03",
      title: "Abnahme durchführen",
      desc: "Wir fahren zum Kunden, führen die komplette Abnahme durch, erstellen die Dokumentation und weisen den Kunden ein.",
    },
    {
      num: "04",
      title: "Dokumente erhalten",
      desc: "Sie erhalten alle Protokolle und Unterlagen digital. Ihr Kunde ist glücklich, Sie können die Schlussrechnung stellen.",
    },
  ];

  return (
    <section id="zusammenarbeit" data-testid="zusammenarbeit-section" className="py-24 sm:py-32 bg-[#121826]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-4">
            So funktioniert's
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
            In 4 Schritten zum erfolgreichen Projektabschluss
          </h2>
        </div>

        <div className="relative">
          {/* Timeline Line (Desktop) */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#39FF14] to-[#00CED1] transform -translate-x-1/2" />

          <div className="space-y-12 md:space-y-0">
            {steps.map((step, index) => (
              <div
                key={index}
                data-testid={`step-${index}`}
                className={`md:flex items-center gap-8 ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? "md:text-right md:pr-16" : "md:text-left md:pl-16"}`}>
                  <div className={`bg-[#0a0f1a] border border-white/10 rounded-2xl p-8 card-hover inline-block text-left w-full md:max-w-md ${index % 2 === 0 ? "md:ml-auto" : "md:mr-auto"}`}>
                    <span className="text-4xl font-black text-[#39FF14]/20">{step.num}</span>
                    <h3 className="text-xl font-semibold text-white mt-2 mb-3">{step.title}</h3>
                    <p className="text-gray-400">{step.desc}</p>
                  </div>
                </div>
                
                {/* Timeline Dot */}
                <div className="hidden md:flex w-12 h-12 rounded-full bg-[#0a0f1a] border-4 border-[#39FF14] items-center justify-center timeline-dot flex-shrink-0 z-10">
                  <span className="text-xs font-bold text-[#39FF14]">{step.num}</span>
                </div>
                
                <div className="flex-1 hidden md:block" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Leistungsumfang Section (Accordion)
const LeistungsumfangSection = () => {
  const leistungen = [
    {
      title: "Technische Prüfung",
      items: [
        "Sichtprüfung der gesamten Anlage",
        "DC-Messungen (Uoc, Isc, Isolationswiderstand)",
        "AC-Messungen (Netzimpedanz, RCD-Test)",
        "Prüfung Potentialausgleich/Erdung",
        "Funktionstest Wechselrichter & Speicher",
        "Prüfung Überspannungsschutz",
      ],
    },
    {
      title: "Dokumentation",
      items: [
        "Inbetriebnahmeprotokoll",
        "Prüfprotokoll nach VDE",
        "Fotodokumentation der Anlage",
        "Stringplan-Verifizierung",
        "Mängelliste (falls zutreffend)",
      ],
    },
    {
      title: "Behördliches",
      items: [
        "Fertigmeldung Netzbetreiber",
        "Hilfe bei Marktstammdatenregister",
        "Konformitätsnachweise zusammenstellen",
      ],
    },
    {
      title: "Kundenbetreuung",
      items: [
        "Vollständige Anlageneinweisung",
        "Erklärung Monitoring/App",
        "Beantwortung technischer Fragen",
        "Tipps zur Eigenverbrauchsoptimierung",
        "Übergabe aller Dokumente",
      ],
    },
    {
      title: "Zusatzleistungen (optional)",
      items: [
        "Identifikation von Erweiterungspotenzial (Speicher, Wallbox)",
        "Vorqualifizierte Leads für Folgeaufträge",
        "Hochwertige Fotos/Videos für Ihr Marketing",
        "Wiederholungsprüfungen nach DGUV V3",
      ],
    },
  ];

  return (
    <section id="leistungen" data-testid="leistungen-section" className="py-24 sm:py-32 bg-[#0a0f1a]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-4">
            Leistungsumfang
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Was genau wir für Sie übernehmen
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {leistungen.map((leistung, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              data-testid={`leistung-accordion-${index}`}
              className="bg-[#121826] border border-white/10 rounded-xl overflow-hidden"
            >
              <AccordionTrigger className="px-6 py-5 text-lg font-semibold text-white hover:text-[#39FF14] hover:no-underline accordion-trigger">
                {leistung.title}
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <ul className="space-y-3">
                  {leistung.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-400">
                      <CheckCircle2 className="w-5 h-5 text-[#00CED1] mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Kooperationsmodelle Section
const KooperationsmodelleSection = () => {
  const modelle = [
    {
      title: "Modell A: Projekt-Basis",
      features: [
        "Sie melden einzelne Projekte nach Bedarf",
        "Keine Mindestabnahme, keine Verpflichtung",
      ],
      ideal: "Gelegentliche Unterstützung",
      highlight: false,
    },
    {
      title: "Modell B: Rahmenvereinbarung",
      features: [
        "Festes Kontingent pro Monat",
        "Bevorzugte Terminvergabe",
        "Attraktivere Konditionen",
      ],
      ideal: "Regelmäßige Zusammenarbeit",
      highlight: true,
    },
    {
      title: "Modell C: Exklusiv-Partner",
      features: [
        "ENTHIDA als fester Abnahme-Partner",
        "Höchste Priorität und Flexibilität",
        "Gemeinsames Marketing möglich",
      ],
      ideal: "Strategische Partnerschaft",
      highlight: false,
    },
  ];

  return (
    <section id="modelle" data-testid="modelle-section" className="py-24 sm:py-32 bg-[#121826]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-4">
            Kooperationsmodelle
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Flexible Zusammenarbeit – wie es zu Ihnen passt
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {modelle.map((modell, index) => (
            <div
              key={index}
              data-testid={`modell-card-${index}`}
              className={`rounded-2xl p-8 card-hover ${
                modell.highlight
                  ? "bg-gradient-to-b from-[#00CED1]/10 to-[#0a0f1a] border-2 border-[#00CED1] glow-secondary"
                  : "bg-[#0a0f1a] border border-white/10"
              }`}
            >
              {modell.highlight && (
                <span className="inline-block text-xs font-bold uppercase tracking-wider text-[#00CED1] mb-4">
                  Empfohlen
                </span>
              )}
              <h3 className="text-xl font-semibold text-white mb-6">{modell.title}</h3>
              <ul className="space-y-3 mb-8">
                {modell.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-400">
                    <CheckCircle2 className="w-5 h-5 text-[#39FF14] mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-6 border-t border-white/10">
                <p className="text-sm text-gray-500">Ideal für:</p>
                <p className="text-white font-medium">{modell.ideal}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 mt-12 max-w-2xl mx-auto">
          Welches Modell passt zu Ihnen? Lassen Sie uns sprechen – wir finden die richtige Lösung für Ihre Situation.
        </p>
      </div>
    </section>
  );
};

// Warum ENTHIDA Section
const WarumEnthidaSection = () => {
  const gruende = [
    { title: "Elektromeister-Qualifikation", desc: "Enes Dedic ist geprüfter Elektromeister mit Spezialisierung auf PV-Systeme. Keine Hilfskräfte, keine Kompromisse." },
    { title: "PV ist unser Fokus", desc: "Wir machen nicht \"auch PV nebenbei\". Der technische Abschluss von Photovoltaikanlagen ist unser Kerngeschäft." },
    { title: "Zuverlässigkeit", desc: "Termine werden eingehalten. Dokumente kommen pünktlich. Rückrufe erfolgen am selben Tag." },
    { title: "Transparente Kommunikation", desc: "Sie wissen immer, wo Ihre Projekte stehen. Kurze Wege, schnelle Antworten." },
    { title: "Regional verwurzelt", desc: "Einsatzgebiet: Eggenfelden + 100km Umkreis in Bayern. Persönlich, nicht anonym." },
  ];

  return (
    <section id="warum-enthida" data-testid="warum-enthida-section" className="py-24 sm:py-32 bg-[#0a0f1a] grain-texture overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-4">
              Warum ENTHIDA?
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-8">
              Was uns auszeichnet
            </h2>
            
            <div className="space-y-6">
              {gruende.map((grund, index) => (
                <div key={index} data-testid={`grund-${index}`} className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#39FF14]/10 flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-[#39FF14]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{grund.title}</h3>
                    <p className="text-gray-400 text-sm">{grund.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img
                src={WHY_ENTHIDA_IMG}
                alt="Professioneller Elektriker bei der Arbeit"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1a] via-transparent to-transparent" />
            </div>
            {/* Decorative glow */}
            <div className="absolute -inset-4 bg-[#39FF14]/5 rounded-3xl blur-3xl -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      q: "Was kostet eine Abnahme?",
      a: "Unsere Preise richten sich nach Anlagengröße:\n\n• Kleinanlagen (bis 10 kWp): ab 149 €\n• Mittlere Anlagen (10-20 kWp): ab 199 €\n• Größere Anlagen (20+ kWp): ab 249 €\n\nBei Rahmenvereinbarungen bieten wir attraktive Pauschalpreise. Sprechen Sie uns an – wir erstellen Ihnen gerne ein individuelles Angebot.",
    },
    {
      q: "Wie schnell können Sie einen Termin machen?",
      a: "Standard: Innerhalb von 5-7 Werktagen.\n\nExpress-Service: Wenn's brennt, sind wir auch kurzfristiger verfügbar – oft innerhalb von 24-48 Stunden. Rufen Sie einfach an, wir finden eine Lösung!",
    },
    {
      q: "Gibt es einen Express-Service?",
      a: "Ja! Für dringende Fälle bieten wir einen Express-Service an. Gegen einen Aufpreis von 50 € können wir die Abnahme oft schon am nächsten oder übernächsten Werktag durchführen. Einfach bei der Anfrage \"Express\" erwähnen.",
    },
    {
      q: "Muss ich bei der Abnahme dabei sein?",
      a: "Nein, das ist nicht nötig. Wir koordinieren den Termin direkt mit Ihrem Endkunden, führen die Abnahme durch und senden Ihnen alle Dokumente digital zu. Sie können sich voll auf Ihre nächste Baustelle konzentrieren.",
    },
    {
      q: "Was ist, wenn Mängel gefunden werden?",
      a: "Wir dokumentieren alle Mängel sachlich und lösungsorientiert mit Fotos. Ihr Kunde erfährt davon professionell und konstruktiv – keine Schuldzuweisungen. Wir erklären, was zu tun ist, und Sie können die Nachbesserung planen.",
    },
    {
      q: "Kann ich ENTHIDA meinen Kunden gegenüber als \"mein Team\" vorstellen?",
      a: "Ja, selbstverständlich! Auf Wunsch treten wir diskret als Teil Ihres Unternehmens auf – in neutraler Kleidung, ohne eigenes Branding. Für Ihren Kunden sind wir einfach \"Ihr Techniker\".",
    },
    {
      q: "Wie läuft die Abrechnung?",
      a: "Unkompliziert: Rechnung nach Abschluss des Projekts mit 14 Tagen Zahlungsziel. Bei Rahmenvereinbarungen ist auch monatliche Sammelrechnung möglich.",
    },
    {
      q: "Welche Regionen decken Sie ab?",
      a: "Unser Kerngebiet ist Eggenfelden und ca. 100 km Umkreis – das umfasst ganz Niederbayern, große Teile Oberbayerns und angrenzende Gebiete. Bei größeren Projekten oder Rahmenvereinbarungen fahren wir auch weiter.",
    },
    {
      q: "Was passiert, wenn der Kunde nicht da ist?",
      a: "Kein Problem – wir können die technische Prüfung auch ohne Anwesenheit des Kunden durchführen, wenn Sie uns Zugang verschaffen. Die Kundeneinweisung holen wir dann telefonisch oder bei einem zweiten Kurztermin nach.",
    },
    {
      q: "Wie erhalte ich die Dokumentation?",
      a: "Alle Unterlagen (Protokolle, Messwerte, Fotos) erhalten Sie innerhalb von 24 Stunden digital per E-Mail als PDF. Auf Wunsch auch als ausgedruckte Mappe für Ihren Kunden.",
    },
  ];

  return (
    <section id="faq" data-testid="faq-section" className="py-24 sm:py-32 bg-[#121826]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-4">
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Häufige Fragen
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`faq-${index}`}
              data-testid={`faq-accordion-${index}`}
              className="bg-[#0a0f1a] border border-white/10 rounded-xl overflow-hidden"
            >
              <AccordionTrigger className="px-6 py-5 text-base font-medium text-white hover:text-[#39FF14] hover:no-underline text-left accordion-trigger">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 text-gray-400 whitespace-pre-line">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Referenzen/Vertrauen Section
const VertrauenSection = () => {
  const badges = [
    { icon: Award, label: "Elektromeister-Qualifikation" },
    { icon: Shield, label: "Berufshaftpflicht versichert" },
    { icon: FileCheck, label: "VDE-konforme Prüfungen" },
    { icon: FileText, label: "Marktstammdatenregister-Beratung" },
  ];

  return (
    <section id="vertrauen" data-testid="vertrauen-section" className="py-24 sm:py-32 bg-[#0a0f1a]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-4">
            Vertrauen
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
            Vertrauen durch Qualität
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Wir arbeiten bereits mit Solarfirmen und Elektrobetrieben in der Region zusammen. 
            Auf Wunsch stellen wir gerne den Kontakt für ein Referenzgespräch her.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {badges.map((badge, index) => (
            <div
              key={index}
              data-testid={`badge-${index}`}
              className="bg-[#121826] border border-white/10 rounded-xl p-6 text-center card-hover"
            >
              <div className="w-14 h-14 rounded-full bg-[#39FF14]/10 flex items-center justify-center mx-auto mb-4">
                <badge.icon className="w-7 h-7 text-[#39FF14]" />
              </div>
              <p className="text-sm font-medium text-white">{badge.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section id="kontakt" data-testid="cta-section" className="py-24 sm:py-32 bg-[#121826] cta-glow overflow-hidden">
    <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
      <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00CED1] mb-4">
        Jetzt starten
      </span>
      <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6">
        Bereit für eine<br />
        <span className="text-[#39FF14] text-glow-primary">Zusammenarbeit?</span>
      </h2>
      <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
        Lassen Sie uns unverbindlich sprechen. Ein kurzes Telefonat genügt, um herauszufinden, 
        ob und wie wir zusammenarbeiten können.
      </p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
        <a
          href="tel:+4917641234614"
          data-testid="cta-phone"
          className="bg-[#39FF14] text-black font-bold px-8 py-4 rounded-full hover:bg-[#32e011] hover:shadow-[0_0_20px_rgba(57,255,20,0.4)] transition-all flex items-center justify-center gap-3"
        >
          <Phone className="w-5 h-5" />
          +49 176 41234 614
        </a>
        <a
          href="https://wa.me/4917641234614"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cta-whatsapp"
          className="bg-[#00CED1] text-black font-bold px-8 py-4 rounded-full hover:bg-[#00b8bc] hover:shadow-[0_0_20px_rgba(0,206,209,0.4)] transition-all flex items-center justify-center gap-3"
        >
          <MessageCircle className="w-5 h-5" />
          WhatsApp schreiben
        </a>
        <a
          href="mailto:info@enthida.de"
          data-testid="cta-email"
          className="border border-white/20 text-white font-bold px-8 py-4 rounded-full hover:border-white hover:bg-white/5 transition-all flex items-center justify-center gap-3"
        >
          <Mail className="w-5 h-5" />
          info@enthida.de
        </a>
      </div>

      <p className="text-sm text-gray-500">
        Oder senden Sie uns direkt Ihr erstes Projekt – wir melden uns umgehend mit einem Angebot.
      </p>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer data-testid="footer" className="py-16 bg-[#05080c] border-t border-white/5 no-print">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
        <div>
          <img src={LOGO_URL} alt="ENTHIDA Logo" className="h-12 w-auto mb-4" />
          <p className="text-gray-500 text-sm">
            Smart Energy Systems<br />
            Elektromeister Enes Dedic
          </p>
        </div>
        
        <div>
          <h4 className="text-white font-semibold mb-4">Kontakt</h4>
          <ul className="space-y-2 text-sm text-gray-500">
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <a href="tel:+4917641234614" className="hover:text-[#39FF14] transition-colors">
                +49 176 41234 614
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <a href="mailto:info@enthida.de" className="hover:text-[#39FF14] transition-colors">
                info@enthida.de
              </a>
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>Eggenfelden + 100km / Bayern</span>
            </li>
          </ul>
        </div>
        
        <div>
          <h4 className="text-white font-semibold mb-4">Rechtliches</h4>
          <ul className="space-y-2 text-sm text-gray-500">
            <li>
              <button 
                onClick={() => document.getElementById("impressum-modal")?.classList.remove("hidden")}
                className="hover:text-[#39FF14] transition-colors"
                data-testid="footer-impressum-link"
              >
                Impressum
              </button>
            </li>
            <li>
              <button 
                onClick={() => document.getElementById("datenschutz-modal")?.classList.remove("hidden")}
                className="hover:text-[#39FF14] transition-colors"
                data-testid="footer-datenschutz-link"
              >
                Datenschutz
              </button>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="pt-8 border-t border-white/5 text-center">
        <p className="text-xs text-gray-600">
          © {new Date().getFullYear()} ENTHIDA – Smart Energy Systems. Alle Rechte vorbehalten.
        </p>
      </div>
    </div>
  </footer>
);

// Floating Contact Button (Mobile)
const FloatingContactButton = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed bottom-20 right-4 z-50 lg:hidden no-print">
      {/* Contact Options */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 flex flex-col gap-3 mb-2">
          <a
            href="tel:+4917641234614"
            className="w-12 h-12 bg-[#39FF14] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            data-testid="floating-phone"
          >
            <Phone className="w-5 h-5 text-black" />
          </a>
          <a
            href="https://wa.me/4917641234614"
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            data-testid="floating-whatsapp"
          >
            <MessageCircle className="w-5 h-5 text-white" />
          </a>
          <a
            href="mailto:info@enthida.de"
            className="w-12 h-12 bg-[#00CED1] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            data-testid="floating-email"
          >
            <Mail className="w-5 h-5 text-black" />
          </a>
        </div>
      )}
      
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="floating-contact-toggle"
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${
          isOpen 
            ? "bg-white/10 backdrop-blur-xl border border-white/20" 
            : "bg-[#39FF14] glow-primary"
        }`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Phone className="w-6 h-6 text-black" />
        )}
      </button>
    </div>
  );
};

// Main App Component
function App() {
  // Layer 3: React Hook Defense
  useRemoveWatermark();
  
  const closeImpressum = () => document.getElementById("impressum-modal")?.classList.add("hidden");
  const closeDatenschutz = () => document.getElementById("datenschutz-modal")?.classList.add("hidden");

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <Header />
      <main>
        <HeroSection />
        <ProblemSection />
        <LoesungSection />
        <VorteileSection />
        <ZusammenarbeitSection />
        <LeistungsumfangSection />
        <KooperationsmodelleSection />
        <WarumEnthidaSection />
        <FAQSection />
        <VertrauenSection />
        <CTASection />
      </main>
      <Footer />
      
      {/* Cookie Banner */}
      <CookieBanner />
      
      {/* Floating Contact Button (Mobile) */}
      <FloatingContactButton />
      
      {/* Legal Modals */}
      <Modal id="impressum-modal" title="Impressum" onClose={closeImpressum}>
        <ImpressumContent />
      </Modal>
      <Modal id="datenschutz-modal" title="Datenschutzerklärung" onClose={closeDatenschutz}>
        <DatenschutzContent />
      </Modal>
    </div>
  );
}

export default App;
